import { Graphics } from 'pixi.js';
import { type DuckColor, type DuckSpecies } from '@duck/contracts';

/**
 * THE CRITTER ROSTER (ADR-0024) — the original duck + 9 more "funny goofy animals", all drawn with the
 * SAME procedural technology as the chick: Pixi primitives only (no texture atlas, per ADR-0010), shaded
 * for volume with a darker rim + a lighter top sheen. Every species is authored on ONE SHARED ANCHOR RIG
 * so the rest of the avatar (cosmetics, held items, the talking-mouth tell, walk/dance/sit animation, the
 * speaking ring, name + presence badge) works IDENTICALLY across all of them — only the body art differs.
 *
 * THE RIG (everything is authored facing RIGHT; the owning {@link Critter} flips it via `body.scale.x`):
 *   • body  — an ellipse at (0,0), r≈(22,18). Cosmetic OUTFITS + the held item sit on this.
 *   • head  — a circle at (14,-14), r≈12; crown ≈ (14,-26). Cosmetic HATS sit on the crown.
 *   • eyes  — near (17,-16) + (9,-16) (the glasses cosmetic anchors), so worn glasses line up.
 *   • feet  — around y∈[14,24], roughly x∈[-9,9].
 *   • mouth — a small hinging lower-jaw/lip Graphics; the Critter rotates it open while the citizen talks
 *             (voice). Each builder returns the mouth node + its pivot so the hinge swings the tip down.
 *
 * COLOUR (the 7 tints stay orthogonal to the animal): a species sets a sensible DEFAULT tint in
 * `SPECIES_META`, but the body is recolourable — the {@link CritterPalette} (hex + auto shade/highlight)
 * is derived from the chosen tint. SIGNATURE ACCENTS (a penguin's white belly, a fox's tail-tip, a pig's
 * snout) are FIXED here regardless of tint, so a teal fox or a pink penguin is still unmistakably itself.
 *
 * Keep this in lock-step with `SPECIES_META` in @duck/contracts and ADR-0024.
 */

// ── Shared tint palette (the body base colour per tint). The original SEVEN are unchanged (byte-faithful
//    duck); the NINE added in the 2026-06-22 art revision are juicy-but-friendly cartoon tones, each kept
//    distinct from its neighbours and readable under the contrast-aware contour below. ──
export const DUCK_HEX: Record<DuckColor, number> = {
  // Original seven.
  yellow: 0xffd23f,
  white: 0xf4f4f8,
  brown: 0xb5793a,
  green: 0x6fcf5f,
  blue: 0x5aa9e6,
  pink: 0xff9ecb,
  black: 0x3a3a44,
  // Art revision 2026-06-22 — warm tones, soft pastels, a mid neutral.
  orange: 0xff924c, // juicy tangerine — clearly apart from yellow + brown
  red: 0xed5a5a, // warm coral-red — cute, not fire-engine
  peach: 0xffc6a3, // soft warm peach
  lavender: 0xc6b6f0, // light lilac — distinct from purple + pink
  purple: 0xa977e0, // friendly grape violet
  teal: 0x3fc7c2, // aqua-teal — apart from blue + green
  mint: 0x9be3c0, // pale mint — apart from saturated green + teal
  cream: 0xf5e6c8, // warm off-white — apart from the cold white
  grey: 0x9aa3b0, // mid slate — fills the gap between white and black
};

// ── Fixed accent colours (species signatures — never tinted). ──
const BEAK = 0xff9f1c; // warm orange — the duck/penguin beak + several feet
const WHITE = 0xf4f4f8;
const DARK = 0x141821; // eyes / pupils / dark details
const CATCH = 0xffffff; // eye catch-light
const NOSE = 0xff8fb0; // soft pink — snouts, noses, inner ears, tongues
const MOUTH_IN = 0x7a3b3b; // dark mouth interior, revealed as the jaw opens

/** Linear RGB blend a → b by t∈[0,1] — used once per critter at construction for shade/highlight tones. */
export function mixHex(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return (r << 16) | (g << 8) | bl;
}

/**
 * The body tones derived from a chosen tint: the base + a darker rim/shade + a lighter top sheen, plus a
 * crisp CONTOUR — the GGD-style outline that wraps the silhouette so every critter "pops" and reads at a
 * distance on ANY floor (Art revision 2026-06-22). `outline` is CONTRAST-AWARE: pale tints (white/cream/
 * mint) get a much deeper ink so they no longer dissolve into a light background, while already-dark tints
 * keep a tint-tied edge that never reads as a heavy black sticker. The duck's INTERNAL recipe is untouched;
 * the contour is a thin underlay rim, so the beloved yellow chick only gains a slightly crisper edge.
 */
export interface CritterPalette {
  hex: number;
  shade: number;
  hi: number;
  outline: number;
}

/** Perceived luminance (Rec.601), 0–255 — drives the contrast-aware contour depth. */
function luminance(hex: number): number {
  return 0.299 * ((hex >> 16) & 0xff) + 0.587 * ((hex >> 8) & 0xff) + 0.114 * (hex & 0xff);
}

// A deep, desaturated INK (not pure black) for contours — warm-neutral so it flatters every tint and reads
// as a hand-drawn cartoon outline rather than a hard vector stroke.
const INK = 0x2b2f3a;

export function buildPalette(color: DuckColor): CritterPalette {
  const hex = DUCK_HEX[color] ?? DUCK_HEX.yellow;
  // Contour depth scales with body lightness: dark bodies blend ~0.32 toward ink (a soft tint-tied rim),
  // pale bodies up to ~0.72 (a strong readable edge). Tuned so 'yellow' lands near its old shade (the duck
  // barely changes) while 'white'/'cream' gain a clear, legible outline.
  const lum = luminance(hex);
  const k = lum < 120 ? 0.32 : 0.32 + Math.min(1, (lum - 120) / 135) * 0.4;
  return {
    hex,
    shade: mixHex(hex, 0x000000, 0.34),
    hi: mixHex(hex, 0xffffff, 0.4),
    outline: mixHex(hex, INK, k),
  };
}

/**
 * A citizen's CHARACTER colour as a CSS string for UI text (the chat username tint), LIFTED toward white
 * when the raw body colour would be too dark to read on the chat's near-black surface — so a 'black'
 * critter's name renders as a legible grey, never invisible, while bright tints (yellow/green/pink) pass
 * through unchanged. Dual-coded with the BOLD name weight, so colour is never the ONLY signal of who spoke
 * (Accessibility). Accepts the wire `duckColor` string; an unknown/empty tint falls back to the duck's yellow.
 */
export function nameColorCss(color: string | undefined): string {
  const base = DUCK_HEX[(color ?? '') as DuckColor] ?? DUCK_HEX.yellow;
  const r = (base >> 16) & 0xff;
  const g = (base >> 8) & 0xff;
  const b = base & 0xff;
  // Perceived luminance (Rec.601). Below the floor, lift toward white enough to clear a contrast minimum
  // on the dark chat (slate-950) without washing out tints that are already bright.
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  const FLOOR = 150;
  const lifted = lum < FLOOR ? mixHex(base, 0xffffff, Math.min(0.7, (FLOOR - lum) / 255 + 0.2)) : base;
  return `#${lifted.toString(16).padStart(6, '0')}`;
}

/**
 * One species' static body art (`body`, drawn in z-order legs→…→head→eyes), a `face` overlay (the eyes +
 * snout/beak re-stamped), its hinging `mouth` (the talking tell) and the `mouthPivot`.
 *
 * WHY `face` (Wave 4, finding S6 "skins only cover the torso"): a full-body SKIN cosmetic (galaxy/neon/
 * robot/gold) is mounted by {@link import('./critter').Critter} BETWEEN `body` and `face`, so it can cover
 * the head + torso while the eyes/snout stay legible ON TOP. `face` re-draws those features IDENTICALLY to
 * the body's own, so when NO skin is worn the look is pixel-for-pixel unchanged (the overlay lands exactly
 * on the body's eyes). Appendages (beak tip, tail, ears, spikes) live in `body` and read as accents under
 * a skin — the intentional "costume" look. The beloved duck's `body` recipe is untouched.
 */
export interface CritterArt {
  body: Graphics;
  face: Graphics;
  mouth: Graphics;
  mouthPivot: { x: number; y: number };
}

// ── Small shared drawing helpers (the duck's proven vocabulary, reused for consistency). ──

/** A glossy little eye: a dark dot with a catch-light. */
function drawEye(g: Graphics, x: number, y: number, r = 2.6): void {
  g.circle(x, y, r).fill(DARK);
  g.circle(x + r * 0.34, y - r * 0.34, Math.max(0.6, r * 0.34)).fill({ color: CATCH, alpha: 0.9 });
}

/** The duck's exact bean-body recipe: contour → shade rim → body → lower-body AO band → pale belly → top sheen. */
function drawBeanBody(g: Graphics, p: CritterPalette, bellyAlpha = 0.18): void {
  // Contour underlay (Art revision 2026-06-22): a thin contrast-aware outline ring peeking ~1px beyond the
  // shade so the silhouette reads on any background. Drawn first so the shade + body sit on top of it.
  g.ellipse(0, 1, 23.6, 19.6)
    .fill(p.outline)
    .ellipse(0, 1, 22.5, 18.5)
    .fill(p.shade)
    .ellipse(0, 0, 22, 18)
    .fill(p.hex)
    .ellipse(0, 9, 18, 10)
    .fill({ color: p.shade, alpha: 0.4 })
    // pale belly — pure white (0xffffff), exactly as the original duck (NOT the off-white WHITE accent),
    // so the byte-faithful duck port stays identical; a low-alpha white sheen reads fine on every animal.
    .ellipse(2, 4, 15, 12)
    .fill({ color: 0xffffff, alpha: bellyAlpha })
    .ellipse(-3, -9, 11, 6)
    .fill({ color: p.hi, alpha: 0.45 });
}

/** The duck's exact head recipe: contour → shade rim → head → cheek sheen, at the rig anchor (14,-14) r12. */
function drawHead(g: Graphics, p: CritterPalette): void {
  g.circle(14, -15, 13.4)
    .fill(p.outline)
    .circle(14, -15, 12.4)
    .fill(p.shade)
    .circle(14, -14, 12)
    .fill(p.hex)
    .ellipse(11, -18, 6, 4)
    .fill({ color: p.hi, alpha: 0.5 });
}

/** Two glossy forward eyes at the glasses anchors (so worn specs line up). `narrow` squints them. */
function drawFaceEyes(g: Graphics, p: CritterPalette, r = 2.4, narrow = false): void {
  drawEye(g, 17, -16, r);
  drawEye(g, 9, -16, r);
  if (narrow) {
    // A lazy half-lid across each eye — smug / sleepy.
    g.roundRect(14, -19, 6.5, 2, 1).fill(p.shade);
    g.roundRect(6, -19, 6.5, 2, 1).fill(p.shade);
  }
}

// ── 1) DUCK — the original, ported byte-faithfully (same shapes + z-order as the beloved chick). ──
function buildDuck(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Legs.
  body.roundRect(-7, 14, 4, 10, 2).roundRect(3, 14, 4, 10, 2).fill(BEAK);
  // Wing (drawn before the body, exactly as the original).
  body
    .ellipse(-5, 0, 11, 10)
    .fill({ color: p.shade, alpha: 0.55 })
    .moveTo(-12, -2)
    .lineTo(2, 2)
    .stroke({ color: p.shade, width: 1.4, alpha: 0.6 })
    .moveTo(-11, -5)
    .lineTo(1, -2)
    .stroke({ color: p.hi, width: 1, alpha: 0.4 });
  drawBeanBody(body, p);
  drawHead(body, p);
  // Eye + catch-light.
  body.circle(18, -16, 2.6).fill(DARK).circle(18.9, -16.9, 0.9).fill({ color: CATCH, alpha: 0.9 });
  // Mouth interior + fixed upper mandible.
  body
    .poly([24, -13, 33, -11, 24, -9])
    .fill({ color: 0x7a2f12, alpha: 0.9 })
    .poly([24, -14, 34, -11, 24, -11])
    .fill(BEAK);

  // Face overlay (Wave 4 S6): eye + beak re-stamped so a skin can cover the head while the face stays on
  // top. Identical to the body's own draws, so the no-skin look is unchanged.
  const face = new Graphics();
  face.circle(18, -16, 2.6).fill(DARK).circle(18.9, -16.9, 0.9).fill({ color: CATCH, alpha: 0.9 });
  face.poly([24, -13, 33, -11, 24, -9]).fill({ color: 0x7a2f12, alpha: 0.9 });
  face.poly([24, -14, 34, -11, 24, -11]).fill(BEAK);

  // Lower mandible — hinges open while talking (pivot at the mouth corner).
  const mouth = new Graphics()
    .poly([0, 0, 10, 0, 0, 3])
    .fill(BEAK)
    .poly([0, 0, 10, 0, 0, 3])
    .fill({ color: 0xcf7a12, alpha: 0.55 });
  return { body, face, mouth, mouthPivot: { x: 24, y: -11 } };
}

// ── 2) FROG — squat, pale-bellied, bulging eyes up top, a huge grin. ──
function buildFrog(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Webbed back feet poking out at the base.
  for (const fx of [-10, 10]) {
    body.ellipse(fx, 21, 6, 3).fill(p.shade).ellipse(fx, 20, 6, 3).fill(p.hex);
  }
  drawBeanBody(body, p, 0);
  // Pale lime belly.
  body.ellipse(2, 6, 14, 11).fill({ color: 0xeaf7d0, alpha: 0.5 });
  drawHead(body, p);
  // Two big bulging eyes high on the head (the frog signature), each its own little dome. Art revision
  // 2026-06-23: a contour ring wraps each dome so the googly eyes POP with the same GGD edge as the body.
  for (const ex of [9, 19]) {
    body.circle(ex, -22, 5.4).fill(p.outline).circle(ex, -22, 4.6).fill(p.shade).circle(ex, -22.6, 4).fill(p.hex);
    body.circle(ex, -23, 2.7).fill(WHITE);
    drawEye(body, ex, -23, 1.5);
  }
  // Tiny nostrils.
  body.circle(11, -15, 0.7).fill(p.shade).circle(17, -15, 0.7).fill(p.shade);
  // A faint cheek blush + the wide mouth interior (revealed when the grin opens).
  body.ellipse(14, -8, 12, 2.6).fill({ color: MOUTH_IN, alpha: 0.55 });

  // Face overlay (Wave 4 S6): the bulging eyes + nostrils re-stamped (identical to the body draws).
  const face = new Graphics();
  for (const ex of [9, 19]) {
    face.circle(ex, -22, 5.4).fill(p.outline).circle(ex, -22, 4.6).fill(p.shade).circle(ex, -22.6, 4).fill(p.hex);
    face.circle(ex, -23, 2.7).fill(WHITE);
    drawEye(face, ex, -23, 1.5);
  }
  face.circle(11, -15, 0.7).fill(p.shade).circle(17, -15, 0.7).fill(p.shade);

  // The wide lower lip — hinges down into a big goofy grin.
  const mouth = new Graphics()
    .moveTo(0, 0)
    .bezierCurveTo(7, 4, 17, 4, 24, 0)
    .lineTo(24, 2)
    .bezierCurveTo(17, 6, 7, 6, 0, 2)
    .closePath()
    .fill(p.shade)
    .moveTo(2, 1)
    .bezierCurveTo(8, 4, 16, 4, 22, 1)
    .stroke({ color: 0x2e7d32, width: 1, alpha: 0.5 });
  return { body, face, mouth, mouthPivot: { x: 2, y: -7 } };
}

// ── 3) CAT — triangle ears, smug half-lidded eyes, whiskers, a curled tail. ──
function buildCat(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Curled tail behind the body.
  body
    .moveTo(-18, 6)
    .bezierCurveTo(-30, 2, -30, -12, -20, -12)
    .stroke({ color: p.shade, width: 5, alpha: 0.9 })
    .moveTo(-18, 6)
    .bezierCurveTo(-29, 2, -29, -11, -20, -11)
    .stroke({ color: p.hex, width: 3 });
  // Paws.
  body.roundRect(-7, 16, 5, 8, 2.5).roundRect(2, 16, 5, 8, 2.5).fill(p.shade);
  drawBeanBody(body, p, 0.12);
  // Cream chest.
  body.ellipse(3, 7, 9, 9).fill({ color: 0xfbf3e4, alpha: 0.5 });
  // Ears (behind the head), with pink inners.
  body.poly([4, -22, 1, -34, 11, -26]).fill(p.shade).poly([5, -22, 3, -32, 11, -26]).fill(p.hex);
  body.poly([24, -22, 27, -34, 17, -26]).fill(p.shade).poly([23, -22, 25, -32, 17, -26]).fill(p.hex);
  body.poly([5, -25, 4, -31, 9, -27]).fill(NOSE).poly([23, -25, 24, -31, 19, -27]).fill(NOSE);
  drawHead(body, p);
  drawFaceEyes(body, p, 2.5, true);
  // Pink nose + the dark mouth interior under it.
  body.poly([12, -10, 16, -10, 14, -7.5]).fill(NOSE);
  body.ellipse(14, -5, 5, 1.6).fill({ color: MOUTH_IN, alpha: 0.5 });
  // Whiskers.
  for (const wy of [-8, -6]) {
    body.moveTo(20, wy).lineTo(31, wy - 1).stroke({ color: WHITE, width: 0.8, alpha: 0.7 });
    body.moveTo(8, wy).lineTo(-3, wy - 1).stroke({ color: WHITE, width: 0.8, alpha: 0.7 });
  }

  // Face overlay (Wave 4 S6): eyes (with half-lids), nose + whiskers re-stamped (identical to body).
  const face = new Graphics();
  drawFaceEyes(face, p, 2.5, true);
  face.poly([12, -10, 16, -10, 14, -7.5]).fill(NOSE);
  face.ellipse(14, -5, 5, 1.6).fill({ color: MOUTH_IN, alpha: 0.5 });
  for (const wy of [-8, -6]) {
    face.moveTo(20, wy).lineTo(31, wy - 1).stroke({ color: WHITE, width: 0.8, alpha: 0.7 });
    face.moveTo(8, wy).lineTo(-3, wy - 1).stroke({ color: WHITE, width: 0.8, alpha: 0.7 });
  }

  // A small lower jaw under the nose.
  const mouth = new Graphics().moveTo(0, 0).bezierCurveTo(3, 3, 7, 3, 10, 0).lineTo(10, 1.5).bezierCurveTo(7, 4.5, 3, 4.5, 0, 1.5).closePath().fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 9, y: -5 } };
}

// ── 4) FOX — big pointed ears, a cream cheek-mask + chest, a sly grin, a white-tipped brush tail. ──
function buildFox(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Big fluffy brush tail with a white tip, sweeping behind.
  body
    .moveTo(-16, 8)
    .bezierCurveTo(-34, 6, -34, -10, -22, -14)
    .bezierCurveTo(-26, -6, -24, 2, -14, 4)
    .closePath()
    .fill(p.shade)
    .moveTo(-15, 7)
    .bezierCurveTo(-31, 5, -31, -8, -21, -12)
    .bezierCurveTo(-25, -5, -23, 1, -13, 3)
    .closePath()
    .fill(p.hex);
  body.ellipse(-27, -10, 5, 6).fill(WHITE); // white tail tip
  // Dark socks / paws.
  body.roundRect(-7, 16, 5, 8, 2).roundRect(2, 16, 5, 8, 2).fill(mixHex(p.shade, DARK, 0.5));
  drawBeanBody(body, p, 0);
  // Cream chest blaze.
  body.ellipse(4, 8, 8, 10).fill({ color: 0xfff4e2, alpha: 0.6 });
  // Tall pointed ears with dark tips.
  body.poly([3, -22, -2, -38, 12, -27]).fill(p.shade).poly([4, -22, 0, -36, 12, -27]).fill(p.hex);
  body.poly([25, -22, 30, -38, 16, -27]).fill(p.shade).poly([24, -22, 28, -36, 16, -27]).fill(p.hex);
  body.poly([1, -33, -2, -38, 4, -35]).fill(DARK).poly([27, -33, 30, -38, 24, -35]).fill(DARK);
  drawHead(body, p);
  // Cream cheek mask around a pointed snout to the right.
  body.poly([22, -18, 34, -12, 22, -6, 16, -12]).fill({ color: 0xfff4e2, alpha: 0.85 });
  drawEye(body, 18, -17, 2.2);
  drawEye(body, 9, -17, 2.2);
  // Black nose at the snout tip + a sly mouth-interior line.
  body.circle(33, -12, 2).fill(DARK);
  body.ellipse(27, -8, 5, 1.4).fill({ color: MOUTH_IN, alpha: 0.5 });

  // Face overlay (Wave 4 S6): cream cheek-mask, eyes, nose + mouth line re-stamped (identical to body).
  const face = new Graphics();
  face.poly([22, -18, 34, -12, 22, -6, 16, -12]).fill({ color: 0xfff4e2, alpha: 0.85 });
  drawEye(face, 18, -17, 2.2);
  drawEye(face, 9, -17, 2.2);
  face.circle(33, -12, 2).fill(DARK);
  face.ellipse(27, -8, 5, 1.4).fill({ color: MOUTH_IN, alpha: 0.5 });

  // A slim lower jaw at the snout — hinges for the sly little yap.
  const mouth = new Graphics().moveTo(0, 0).lineTo(12, 1).lineTo(11, 3).lineTo(0, 2.5).closePath().fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 21, y: -8 } };
}

// ── 5) PENGUIN — tuxedo body (dark back + big white belly), flipper wings, an orange beak + feet. ──
function buildPenguin(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Orange feet splayed at the base.
  body.ellipse(-7, 22, 5, 2.6).fill(BEAK).ellipse(7, 22, 5, 2.6).fill(BEAK);
  body.poly([-11, 21, -3, 21, -7, 24]).fill(mixHex(BEAK, DARK, 0.2));
  body.poly([3, 21, 11, 21, 7, 24]).fill(mixHex(BEAK, DARK, 0.2));
  // Far flipper (behind the body).
  body.ellipse(-19, 2, 4, 11).fill(p.shade);
  drawBeanBody(body, p, 0);
  // The big white tuxedo belly.
  body.ellipse(3, 5, 13.5, 14).fill(WHITE).ellipse(3, 5, 13.5, 14).fill({ color: 0xeef1f6, alpha: 0.35 });
  // Near flipper (on top of the body).
  body.ellipse(18, 3, 4, 11).fill(p.shade).ellipse(18, 3, 4, 11).fill({ color: p.hi, alpha: 0.15 });
  drawHead(body, p);
  // White face patch around the eyes (penguins have a pale face/throat).
  body.ellipse(14, -13, 9, 8).fill({ color: WHITE, alpha: 0.18 });
  drawEye(body, 18, -16, 2.4);
  drawEye(body, 9, -16, 2.4);
  // Fixed upper beak + a little interior wedge.
  body.poly([16, -13, 26, -11, 16, -9]).fill(BEAK);
  body.poly([18, -11, 24, -10.5, 18, -10]).fill({ color: MOUTH_IN, alpha: 0.7 });

  // Face overlay (Wave 4 S6): white face patch, eyes + beak re-stamped (identical to body).
  const face = new Graphics();
  face.ellipse(14, -13, 9, 8).fill({ color: WHITE, alpha: 0.18 });
  drawEye(face, 18, -16, 2.4);
  drawEye(face, 9, -16, 2.4);
  face.poly([16, -13, 26, -11, 16, -9]).fill(BEAK);
  face.poly([18, -11, 24, -10.5, 18, -10]).fill({ color: MOUTH_IN, alpha: 0.7 });

  // Lower beak — hinges open (penguins keep the beak tell, like the duck).
  const mouth = new Graphics().poly([0, 0, 8, 0.5, 0, 2.5]).fill(mixHex(BEAK, DARK, 0.12));
  return { body, face, mouth, mouthPivot: { x: 16, y: -10 } };
}

// ── 6) PIG — a big round snout with two nostrils, floppy ears, a curly tail, rosy cheeks. ──
function buildPig(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Curly tail behind.
  body
    .moveTo(-18, 4)
    .bezierCurveTo(-26, 2, -26, -4, -22, -4)
    .bezierCurveTo(-19, -4, -20, 1, -23, 0)
    .stroke({ color: p.shade, width: 2.4 });
  // Trotters.
  body.roundRect(-7, 16, 5, 8, 2).roundRect(2, 16, 5, 8, 2).fill(p.shade);
  drawBeanBody(body, p, 0.1);
  // Floppy ears (forward-drooping triangles).
  body.poly([2, -24, -4, -16, 9, -20]).fill(p.shade).poly([3, -23, -2, -17, 9, -20]).fill(mixHex(p.hex, NOSE, 0.3));
  body.poly([26, -24, 32, -16, 19, -20]).fill(p.shade).poly([25, -23, 30, -17, 19, -20]).fill(mixHex(p.hex, NOSE, 0.3));
  drawHead(body, p);
  // Rosy cheeks.
  body.circle(6, -11, 2.6).fill({ color: 0xff9bb0, alpha: 0.45 });
  body.circle(22, -11, 2.6).fill({ color: 0xff9bb0, alpha: 0.45 });
  drawFaceEyes(body, p, 2.4);
  // The signature snout disc with two nostrils.
  body.ellipse(15, -9, 7, 5).fill(mixHex(p.hex, NOSE, 0.55)).ellipse(15, -9, 7, 5).stroke({ color: p.shade, width: 1, alpha: 0.4 });
  body.ellipse(12.5, -9, 1.2, 1.8).fill(MOUTH_IN).ellipse(17.5, -9, 1.2, 1.8).fill(MOUTH_IN);

  // Face overlay (Wave 4 S6): rosy cheeks, eyes + snout disc with nostrils re-stamped (identical to body).
  const face = new Graphics();
  face.circle(6, -11, 2.6).fill({ color: 0xff9bb0, alpha: 0.45 });
  face.circle(22, -11, 2.6).fill({ color: 0xff9bb0, alpha: 0.45 });
  drawFaceEyes(face, p, 2.4);
  face.ellipse(15, -9, 7, 5).fill(mixHex(p.hex, NOSE, 0.55)).ellipse(15, -9, 7, 5).stroke({ color: p.shade, width: 1, alpha: 0.4 });
  face.ellipse(12.5, -9, 1.2, 1.8).fill(MOUTH_IN).ellipse(17.5, -9, 1.2, 1.8).fill(MOUTH_IN);

  // A small lower jaw beneath the snout.
  const mouth = new Graphics().moveTo(0, 0).bezierCurveTo(4, 2.5, 10, 2.5, 14, 0).lineTo(14, 1.5).bezierCurveTo(10, 4, 4, 4, 0, 1.5).closePath().fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 7, y: -4 } };
}

// ── 7) AXOLOTL — pale, three frilly external gills each side, a wide perma-smile, dot eyes. ──
function buildAxolotl(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Little legs + a flat tail hint behind.
  body.ellipse(-20, 6, 6, 4).fill(p.shade); // tail base
  body.roundRect(-7, 17, 5, 7, 2.5).roundRect(2, 17, 5, 7, 2.5).fill(p.shade);
  drawBeanBody(body, p, 0.16);
  drawHead(body, p);
  // The iconic frilly gills — three feathery branches sweeping back from each side of the head.
  for (const side of [-1, 1]) {
    const bx = 14 + side * 11;
    for (const [dy, len] of [
      [-22, 12],
      [-16, 13],
      [-10, 11],
    ] as const) {
      const tx = bx + side * len;
      const frill = mixHex(p.hex, 0xff5d8f, 0.4);
      // Fuller frilly stalk (Art revision 2026-06-23): a darker base stroke gives the gill an edge, the
      // pink frill rides on top, and a bigger pom tip reads at a glance instead of a thin string.
      body
        .moveTo(bx, dy)
        .quadraticCurveTo(bx + side * len * 0.6, dy - 5, tx, dy - 2)
        .stroke({ color: mixHex(frill, DARK, 0.3), width: 5, alpha: 0.7 })
        .moveTo(bx, dy)
        .quadraticCurveTo(bx + side * len * 0.6, dy - 5, tx, dy - 2)
        .stroke({ color: frill, width: 3.4, alpha: 0.95 });
      body.circle(tx, dy - 2, 3).fill({ color: mixHex(p.hex, 0xff5d8f, 0.45), alpha: 0.98 });
      body.circle(tx - 0.8, dy - 2.8, 1).fill({ color: WHITE, alpha: 0.5 });
    }
  }
  // Tiny dot eyes, set wide — peak derpy-cute.
  drawEye(body, 19, -16, 1.7);
  drawEye(body, 8, -16, 1.7);
  // Rosy cheeks + the wide smile interior.
  body.circle(6, -11, 2.4).fill({ color: 0xff9ecb, alpha: 0.5 });
  body.circle(22, -11, 2.4).fill({ color: 0xff9ecb, alpha: 0.5 });
  body.ellipse(14, -8, 9, 2).fill({ color: 0xc56b7a, alpha: 0.5 });

  // Face overlay (Wave 4 S6): dot eyes, cheeks + smile interior re-stamped (identical to body). The frilly
  // gills stay in `body` (appendages) and read as accents under a skin.
  const face = new Graphics();
  drawEye(face, 19, -16, 1.7);
  drawEye(face, 8, -16, 1.7);
  face.circle(6, -11, 2.4).fill({ color: 0xff9ecb, alpha: 0.5 });
  face.circle(22, -11, 2.4).fill({ color: 0xff9ecb, alpha: 0.5 });
  face.ellipse(14, -8, 9, 2).fill({ color: 0xc56b7a, alpha: 0.5 });

  // The wide, content perma-smile lower lip.
  const mouth = new Graphics()
    .moveTo(0, 0)
    .bezierCurveTo(6, 4, 14, 4, 20, 0)
    .lineTo(20, 1.6)
    .bezierCurveTo(14, 5.5, 6, 5.5, 0, 1.6)
    .closePath()
    .fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 4, y: -8 } };
}

// ── 8) CAPYBARA — long blocky snout, tiny ears, sleepy half-eyes; the unbothered zen chonk. ──
function buildCapybara(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Stubby legs.
  body.roundRect(-8, 16, 6, 8, 2).roundRect(3, 16, 6, 8, 2).fill(p.shade);
  drawBeanBody(body, p, 0.08);
  // Tiny rounded ears.
  body.circle(5, -23, 3).fill(p.shade).circle(5, -23.5, 2.4).fill(p.hex);
  body.circle(23, -23, 3).fill(p.shade).circle(23, -23.5, 2.4).fill(p.hex);
  drawHead(body, p);
  // The long, blocky muzzle extending right (the capybara silhouette). Art revision 2026-06-23: a contour
  // underlay wraps the muzzle so it pops with the same GGD edge as the head instead of reading as a flat block.
  body.roundRect(19.2, -16.8, 14.6, 12.4, 5).fill(p.outline);
  body.roundRect(20, -16, 13, 11, 4).fill(p.shade).roundRect(20, -16.5, 13, 11, 4).fill(p.hex);
  body.roundRect(20, -13, 13, 5, 3).fill({ color: p.shade, alpha: 0.25 });
  // Sleepy half-lidded eyes (the unbothered look).
  drawEye(body, 12, -16, 2.2);
  drawEye(body, 5, -16, 2.2);
  body.roundRect(9, -18.5, 6.5, 2, 1).fill(p.shade);
  body.roundRect(2, -18.5, 6.5, 2, 1).fill(p.shade);
  // Big calm nose at the muzzle end, with two soft nostrils.
  body.ellipse(32, -11, 3, 2.4).fill(mixHex(p.shade, DARK, 0.4));
  body.circle(31, -11.3, 0.6).fill(DARK).circle(33, -11.3, 0.6).fill(DARK);
  body.ellipse(26, -7, 5, 1.4).fill({ color: MOUTH_IN, alpha: 0.4 });

  // Face overlay (Wave 4 S6): the long muzzle, sleepy eyes + nose re-stamped (identical to body). The
  // muzzle is in the overlay so a skin never leaves it two-toned.
  const face = new Graphics();
  face.roundRect(19.2, -16.8, 14.6, 12.4, 5).fill(p.outline);
  face.roundRect(20, -16, 13, 11, 4).fill(p.shade).roundRect(20, -16.5, 13, 11, 4).fill(p.hex);
  face.roundRect(20, -13, 13, 5, 3).fill({ color: p.shade, alpha: 0.25 });
  drawEye(face, 12, -16, 2.2);
  drawEye(face, 5, -16, 2.2);
  face.roundRect(9, -18.5, 6.5, 2, 1).fill(p.shade);
  face.roundRect(2, -18.5, 6.5, 2, 1).fill(p.shade);
  face.ellipse(32, -11, 3, 2.4).fill(mixHex(p.shade, DARK, 0.4));
  face.circle(31, -11.3, 0.6).fill(DARK).circle(33, -11.3, 0.6).fill(DARK);
  face.ellipse(26, -7, 5, 1.4).fill({ color: MOUTH_IN, alpha: 0.4 });

  // A wide blocky lower jaw on the muzzle.
  const mouth = new Graphics().moveTo(0, 0).lineTo(13, 0.5).lineTo(13, 2.5).lineTo(0, 3).closePath().fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 19, y: -7 } };
}

// ── 9) HAMSTER — fat cheek pouches, tiny round ears, a little pink nose, stubby; the chonky snacker. ──
function buildHamster(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Tiny feet.
  body.roundRect(-6, 17, 4.5, 6, 2).roundRect(1.5, 17, 4.5, 6, 2).fill(NOSE);
  drawBeanBody(body, p, 0.14);
  // Cream tummy.
  body.ellipse(3, 7, 10, 10).fill({ color: 0xfff3da, alpha: 0.5 });
  // Tiny round ears.
  body.circle(6, -24, 3.4).fill(p.shade).circle(6, -24.5, 2.6).fill(p.hex).circle(6, -24.5, 1.3).fill(NOSE);
  body.circle(22, -24, 3.4).fill(p.shade).circle(22, -24.5, 2.6).fill(p.hex).circle(22, -24.5, 1.3).fill(NOSE);
  drawHead(body, p);
  // Fat CREAM cheek pouches bulging low + outward past the face — the stuffed-cheek hamster signature.
  // A pale fill (not the body tint) + a soft outline makes them read as full pouches, never a plain head.
  for (const cxp of [4.5, 23.5]) {
    body.circle(cxp, -9, 6.6).fill({ color: 0xfff3da, alpha: 0.95 });
    body.circle(cxp, -9, 6.6).stroke({ color: p.shade, width: 1, alpha: 0.45 });
  }
  // A cream muzzle patch tying the cheeks together, with a little pink nose on top.
  body.ellipse(14, -10, 6, 4.5).fill({ color: 0xfff3da, alpha: 0.95 });
  // Big round eyes (above the cheeks) + the pink nose + a tiny mouth.
  drawEye(body, 18, -17, 2.6);
  drawEye(body, 9, -17, 2.6);
  body.circle(14, -11.5, 1.7).fill(NOSE).circle(13.4, -12.1, 0.6).fill({ color: WHITE, alpha: 0.8 });
  body.ellipse(14, -8.5, 3.5, 1.2).fill({ color: MOUTH_IN, alpha: 0.45 });

  // Face overlay (Wave 4 S6): the cream cheek pouches, muzzle patch, eyes + pink nose re-stamped (identical
  // to body) so the hamster's signature face rides on top of any skin.
  const face = new Graphics();
  for (const cxp of [4.5, 23.5]) {
    face.circle(cxp, -9, 6.6).fill({ color: 0xfff3da, alpha: 0.95 });
    face.circle(cxp, -9, 6.6).stroke({ color: p.shade, width: 1, alpha: 0.45 });
  }
  face.ellipse(14, -10, 6, 4.5).fill({ color: 0xfff3da, alpha: 0.95 });
  drawEye(face, 18, -17, 2.6);
  drawEye(face, 9, -17, 2.6);
  face.circle(14, -11.5, 1.7).fill(NOSE).circle(13.4, -12.1, 0.6).fill({ color: WHITE, alpha: 0.8 });
  face.ellipse(14, -8.5, 3.5, 1.2).fill({ color: MOUTH_IN, alpha: 0.45 });

  // A tiny lower jaw between the cheeks.
  const mouth = new Graphics().moveTo(0, 0).bezierCurveTo(2.5, 2, 6, 2, 8, 0).lineTo(8, 1.4).bezierCurveTo(6, 3.4, 2.5, 3.4, 0, 1.4).closePath().fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 10, y: -8 } };
}

// ── 10) DINO — a big toothy head, a ridge of spikes down the back, tiny arms; the goofy T-rex. ──
function buildDino(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // A thick tail sweeping behind.
  body
    .moveTo(-16, 10)
    .bezierCurveTo(-34, 8, -36, -2, -24, -4)
    .lineTo(-16, 2)
    .closePath()
    .fill(p.shade)
    .moveTo(-15, 9)
    .bezierCurveTo(-31, 7, -33, -1, -23, -3)
    .lineTo(-15, 2)
    .closePath()
    .fill(p.hex);
  // Stompy hind legs.
  body.roundRect(-8, 15, 6, 9, 2.5).roundRect(3, 15, 6, 9, 2.5).fill(p.shade);
  body.poly([-8, 23, -2, 23, -2, 25, -9, 25]).fill(mixHex(p.shade, DARK, 0.3));
  body.poly([3, 23, 9, 23, 9, 25, 2, 25]).fill(mixHex(p.shade, DARK, 0.3));
  // A ridge of back spikes (behind the body).
  for (const [sx, sy, h] of [
    [-12, -6, 5],
    [-4, -12, 6],
    [5, -16, 6],
  ] as const) {
    body.poly([sx - 3, sy, sx + 3, sy, sx, sy - h]).fill(mixHex(p.hex, 0xffe08a, 0.4));
  }
  drawBeanBody(body, p, 0.06);
  // Pale belly scales.
  body.ellipse(4, 8, 8, 10).fill({ color: 0xeafbe0, alpha: 0.4 });
  // Tiny arm.
  body.roundRect(14, 2, 7, 3, 1.5).fill(p.shade);
  body.circle(21, 3.5, 2).fill(p.hex);
  drawHead(body, p);
  // A big jaw block extending right (the dino snout).
  body.roundRect(18, -14, 16, 10, 3).fill(p.shade).roundRect(18, -14.5, 16, 10, 3).fill(p.hex);
  // Nostril + eye.
  body.circle(31, -12, 1).fill(p.shade);
  drawEye(body, 14, -18, 2.6);
  // Upper teeth along the jaw.
  for (const tx of [21, 25, 29]) body.poly([tx, -5, tx + 2.4, -5, tx + 1.2, -2.5]).fill(WHITE);
  body.rect(18, -6, 16, 1.4).fill({ color: MOUTH_IN, alpha: 0.6 });

  // Face overlay (Wave 4 S6): the jaw block, nostril, eye + upper teeth re-stamped (identical to body) so
  // the snout never goes two-toned under a skin. The back spikes stay in `body` as accents.
  const face = new Graphics();
  face.roundRect(18, -14, 16, 10, 3).fill(p.shade).roundRect(18, -14.5, 16, 10, 3).fill(p.hex);
  face.circle(31, -12, 1).fill(p.shade);
  drawEye(face, 14, -18, 2.6);
  for (const tx of [21, 25, 29]) face.poly([tx, -5, tx + 2.4, -5, tx + 1.2, -2.5]).fill(WHITE);
  face.rect(18, -6, 16, 1.4).fill({ color: MOUTH_IN, alpha: 0.6 });

  // A toothy lower jaw — hinges wide for the goofy "rawr".
  const mouth = new Graphics();
  mouth.moveTo(0, 0).lineTo(16, 0).lineTo(16, 4).lineTo(0, 4).closePath().fill(p.shade);
  for (const tx of [3, 7, 11]) mouth.poly([tx, 0, tx + 2.4, 0, tx + 1.2, 2.5]).fill(WHITE);
  return { body, face, mouth, mouthPivot: { x: 18, y: -5 } };
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// WAVE 2 — the unhinged & delightful (ADR-0036). Ten more critters on the SAME shared rig + the same
// proven vocabulary (drawBeanBody/drawHead/drawEye, a `face` overlay re-stamp, a hinging `mouth`). Each
// leans into a meme-worthy silhouette so it reads at a glance and earns a grin (Creative + Art Director).
// ════════════════════════════════════════════════════════════════════════════════════════════════

// ── 11) GOOSE — it's a duck, but ANGRY. A long orange knobbed beak, an S-curved neck, a slanted brow,
// and a crisp FOLDED WING on the flank (Art pass): rounded wrist up front → three pointed primary
// feathers off the back, covert lines + a top sheen for real feathered volume. Pure HONK menace. ──
function buildGoose(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Big webbed orange feet.
  body.roundRect(-7, 14, 5, 10, 2).roundRect(3, 14, 5, 10, 2).fill(BEAK);
  drawBeanBody(body, p);
  // FOLDED WING on the near flank, drawn ON TOP of the body so it actually reads — the old wing sat
  // BEHIND the opaque body and vanished (the "кривое крыло"). A contour rim lifts it off even a white
  // goose; covert lines + primary tips + a sheen give it feathered volume.
  const wingFill = mixHex(p.hex, p.shade, 0.3);
  body
    // contour rim (backing) — an almond swept from the shoulder wrist back to the tail.
    .moveTo(9, -7)
    .bezierCurveTo(1, -12, -12, -10, -19, 1)
    .bezierCurveTo(-16, 5, -12, 8, -6, 8)
    .bezierCurveTo(1, 8, 7, 3, 10, -3)
    .closePath()
    .fill(p.outline)
    // wing surface, inset a hair.
    .moveTo(8.5, -6)
    .bezierCurveTo(1, -10.5, -11, -8.5, -17.3, 1)
    .bezierCurveTo(-14.6, 4.4, -11, 6.6, -6, 6.6)
    .bezierCurveTo(0.5, 6.6, 6, 2.4, 8.6, -3)
    .closePath()
    .fill(wingFill);
  // Three pointed PRIMARY feather tips fanning off the trailing (back) edge.
  body.poly([-18.5, 0.5, -12.5, -1.5, -13.5, 3]).fill(p.outline);
  body.poly([-17.5, 3.5, -11, 2, -12.5, 6.5]).fill(p.outline);
  body.poly([-14.5, 6.5, -8, 5.5, -10, 9]).fill(p.outline);
  // Covert feather lines sweeping toward the tip + a soft top sheen.
  for (const [sx, sy, ex, ey] of [
    [5, -4, -11, -1],
    [4, 0, -12, 3],
    [1, 3.5, -10, 6],
  ] as const)
    body.moveTo(sx, sy).quadraticCurveTo((sx + ex) / 2, (sy + ey) / 2 - 1.5, ex, ey).stroke({ color: p.shade, width: 1, alpha: 0.55 });
  body.ellipse(-1, -3, 8, 3.4).fill({ color: p.hi, alpha: 0.4 });
  // Long S-curved goose NECK rising from the chest into the head (Art revision 2026-06-23) — the signature
  // that separates the goose from "an angry duck". Drawn between body + head so the head sits on top; a
  // contour underlay + a top highlight give it the same chunky volume as the body. The head anchor is
  // UNCHANGED (14,-14), so cosmetics/glasses/the mouth still line up.
  body
    .moveTo(10, 11)
    .bezierCurveTo(25, 5, 24, -7, 15, -11)
    .stroke({ color: p.outline, width: 11 })
    .moveTo(10, 11)
    .bezierCurveTo(25, 5, 24, -7, 15, -11)
    .stroke({ color: p.hex, width: 8 })
    .moveTo(11, 9)
    .bezierCurveTo(22, 4, 21, -6, 15, -9)
    .stroke({ color: p.hi, width: 2, alpha: 0.4 });
  drawHead(body, p);
  const stampFace = (g: Graphics): void => {
    // A menacing eye with an angry slanted brow.
    drawEye(g, 18, -16, 2.4);
    g.moveTo(13, -21).lineTo(22, -18).stroke({ color: DARK, width: 1.8, alpha: 0.85 });
    // Long orange beak with a knob at the base + a dark nostril (the goose tell).
    g.poly([22, -14, 22, -16.5, 26, -15]).fill(mixHex(BEAK, DARK, 0.18)); // knob
    g.poly([24, -14, 36, -12, 24, -10]).fill(BEAK);
    g.circle(28, -12.5, 0.9).fill({ color: 0x9a5a10, alpha: 0.85 }); // nostril
  };
  stampFace(body);
  const face = new Graphics();
  stampFace(face);
  // Long lower mandible — hinges wide for the HONK.
  const mouth = new Graphics().poly([0, 0, 12, 0, 0, 3]).fill(mixHex(BEAK, DARK, 0.06));
  return { body, face, mouth, mouthPivot: { x: 24, y: -11 } };
}

// ── 12) SHIBA — doge. Upright pointed ears, a curly tail, a cream muzzle, a smug squint. So wow. ──
function buildShiba(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Curly tail looping over the back.
  body
    .moveTo(-16, 2)
    .bezierCurveTo(-28, 0, -28, -16, -16, -16)
    .bezierCurveTo(-10, -16, -10, -7, -16, -8)
    .stroke({ color: p.shade, width: 6 })
    .moveTo(-16, 2)
    .bezierCurveTo(-27, 0, -27, -15, -16, -15)
    .bezierCurveTo(-11, -15, -11, -8, -16, -9)
    .stroke({ color: p.hex, width: 4 });
  // Paws.
  body.roundRect(-7, 16, 5, 8, 2.5).roundRect(2, 16, 5, 8, 2.5).fill(p.shade);
  drawBeanBody(body, p, 0.1);
  // Cream chest.
  body.ellipse(3, 8, 8, 9).fill({ color: 0xfdf0dd, alpha: 0.6 });
  // Upright pointed ears with pink inners.
  body.poly([3, -23, 0, -36, 12, -27]).fill(p.shade).poly([4, -23, 2, -34, 12, -27]).fill(p.hex);
  body.poly([25, -23, 28, -36, 16, -27]).fill(p.shade).poly([24, -23, 26, -34, 16, -27]).fill(p.hex);
  body.poly([4, -27, 3, -33, 8, -29]).fill(NOSE).poly([24, -27, 25, -33, 20, -29]).fill(NOSE);
  drawHead(body, p);
  const stampFace = (g: Graphics): void => {
    // Cream muzzle/cheek mask.
    g.ellipse(14, -10, 9, 6).fill({ color: 0xfdf0dd, alpha: 0.9 });
    // Smug half-lidded eyes.
    drawFaceEyes(g, p, 2.3, true);
    // Black nose.
    g.ellipse(14, -12, 1.9, 1.4).fill(DARK).circle(13.3, -12.4, 0.5).fill({ color: WHITE, alpha: 0.7 });
    // Smug mouth interior.
    g.ellipse(14, -7.5, 4, 1.3).fill({ color: MOUTH_IN, alpha: 0.45 });
  };
  stampFace(body);
  const face = new Graphics();
  stampFace(face);
  const mouth = new Graphics().moveTo(0, 0).bezierCurveTo(3, 2.5, 7, 2.5, 10, 0).lineTo(10, 1.5).bezierCurveTo(7, 4, 3, 4, 0, 1.5).closePath().fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 9, y: -6 } };
}

// ── 13) SLOTH — long hanging arms with claws, dark eye-patches, a content sleepy smile. No rush, ever. ──
function buildSloth(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Long shaggy arms hanging at the sides, ending in big claws (Art revision 2026-06-23: thicker, contoured
  // and BODY-coloured so they read as proper limbs — not thin dark strings against the chunky bean).
  for (const [sx, ex] of [
    [-14, -12],
    [16, 14],
  ] as const) {
    const c1x = sx < 0 ? sx - 6 : sx + 6;
    body.moveTo(sx, -1).bezierCurveTo(c1x, 8, ex - 2, 16, ex, 20).stroke({ color: p.outline, width: 9 });
    body.moveTo(sx, -1).bezierCurveTo(c1x, 8, ex - 2, 16, ex, 20).stroke({ color: p.hex, width: 6 });
  }
  for (const cx of [-12, 14]) {
    body.moveTo(cx - 2.5, 19).lineTo(cx - 4, 25).moveTo(cx + 1.5, 19).lineTo(cx + 3, 25).stroke({ color: mixHex(p.shade, DARK, 0.45), width: 2.2 });
  }
  body.roundRect(-6, 17, 5, 7, 2.5).roundRect(1, 17, 5, 7, 2.5).fill(p.shade);
  drawBeanBody(body, p, 0.12);
  drawHead(body, p);
  const stampFace = (g: Graphics): void => {
    // Dark eye-patches (the sloth mask).
    g.ellipse(18, -16, 4.5, 5).fill({ color: mixHex(p.shade, DARK, 0.45), alpha: 0.55 });
    g.ellipse(9, -16, 4.5, 5).fill({ color: mixHex(p.shade, DARK, 0.45), alpha: 0.55 });
    drawEye(g, 18, -16, 2);
    drawEye(g, 9, -16, 2);
    // Tiny nose + a content closed smile.
    g.ellipse(14, -11, 2, 1.5).fill(mixHex(p.shade, DARK, 0.5));
    g.moveTo(9, -8).quadraticCurveTo(14, -5, 19, -8).stroke({ color: MOUTH_IN, width: 1.2, alpha: 0.6 });
  };
  stampFace(body);
  const face = new Graphics();
  stampFace(face);
  const mouth = new Graphics().moveTo(0, 0).bezierCurveTo(4, 2, 9, 2, 12, 0).lineTo(12, 1.4).bezierCurveTo(9, 3.4, 4, 3.4, 0, 1.4).closePath().fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 8, y: -7 } };
}

// ── 14) RACCOON — the trash panda: a black bandit mask, a ringed bushy tail, a pointed snack-seeking snout. ──
function buildRaccoon(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Ringed bushy tail.
  body
    .moveTo(-14, 6)
    .bezierCurveTo(-30, 6, -32, -10, -22, -14)
    .bezierCurveTo(-26, -6, -22, 2, -12, 4)
    .closePath()
    .fill(p.shade)
    .moveTo(-14, 6)
    .bezierCurveTo(-29, 6, -31, -9, -22, -13)
    .bezierCurveTo(-25, -6, -21, 2, -12, 3)
    .closePath()
    .fill(p.hex);
  for (const rx of [-25, -19]) body.ellipse(rx, -5, 2.4, 6).fill({ color: mixHex(p.shade, DARK, 0.5), alpha: 0.8 }); // dark rings
  body.ellipse(-27, -11, 4.5, 5).fill(mixHex(p.shade, DARK, 0.6)); // dark tip
  body.roundRect(-7, 16, 5, 8, 2).roundRect(2, 16, 5, 8, 2).fill(p.shade);
  drawBeanBody(body, p, 0.14);
  body.ellipse(3, 8, 8, 9).fill({ color: 0xdfe3ea, alpha: 0.4 }); // pale belly
  // Pointed ears with pale inners.
  body.poly([3, -23, 0, -34, 11, -26]).fill(p.shade).poly([4, -23, 2, -32, 11, -26]).fill(p.hex);
  body.poly([25, -23, 28, -34, 17, -26]).fill(p.shade).poly([24, -23, 26, -32, 17, -26]).fill(p.hex);
  body.poly([4, -27, 3, -32, 8, -28]).fill({ color: WHITE, alpha: 0.7 }).poly([24, -27, 25, -32, 20, -28]).fill({ color: WHITE, alpha: 0.7 });
  drawHead(body, p);
  const stampFace = (g: Graphics): void => {
    // Pale brow + the black bandit mask across both eyes (the raccoon signature).
    g.ellipse(14, -17, 11, 5).fill({ color: WHITE, alpha: 0.55 });
    g.ellipse(18, -16, 5, 4).fill({ color: DARK, alpha: 0.82 });
    g.ellipse(9, -16, 5, 4).fill({ color: DARK, alpha: 0.82 });
    drawEye(g, 18, -16, 2.1);
    drawEye(g, 9, -16, 2.1);
    // Pale pointed snout + a black nose tip.
    g.poly([14, -12, 26, -10, 14, -7]).fill({ color: WHITE, alpha: 0.5 });
    g.circle(25, -10, 1.8).fill(DARK);
  };
  stampFace(body);
  const face = new Graphics();
  stampFace(face);
  const mouth = new Graphics().moveTo(0, 0).lineTo(10, 1).lineTo(9, 3).lineTo(0, 2.5).closePath().fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 16, y: -8 } };
}

// ── 15) OWL — huge facial-disc eyes, ear tufts, folded wings, a tiny beak. The night-shift legend. ──
function buildOwl(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Folded wings at the sides.
  body.ellipse(-16, 2, 5, 12).fill(p.shade).ellipse(-15, 2, 4, 11).fill(p.hex);
  body.ellipse(20, 2, 5, 12).fill(p.shade).ellipse(19, 2, 4, 11).fill(p.hex);
  // Taloned feet.
  body.roundRect(-6, 16, 4, 7, 1.5).roundRect(2, 16, 4, 7, 1.5).fill(BEAK);
  drawBeanBody(body, p, 0.1);
  // Pale speckled chest.
  body.ellipse(3, 7, 9, 10).fill({ color: 0xf3e6cf, alpha: 0.4 });
  // Ear tufts.
  body.poly([5, -22, 1, -33, 10, -26]).fill(p.shade).poly([5, -22, 3, -31, 10, -26]).fill(p.hex);
  body.poly([23, -22, 27, -33, 18, -26]).fill(p.shade).poly([23, -22, 25, -31, 18, -26]).fill(p.hex);
  drawHead(body, p);
  const stampFace = (g: Graphics): void => {
    // Two big facial discs nearly filling the face.
    for (const dx of [18, 9]) {
      g.circle(dx, -15, 6.5).fill({ color: 0xf3e6cf, alpha: 0.6 }).circle(dx, -15, 6.5).stroke({ color: p.shade, width: 1, alpha: 0.5 });
      g.circle(dx, -15, 4).fill(WHITE);
      drawEye(g, dx, -15, 2.6);
    }
    // Small orange beak between the eyes.
    g.poly([12, -12, 16, -12, 14, -7]).fill(BEAK);
  };
  stampFace(body);
  const face = new Graphics();
  stampFace(face);
  const mouth = new Graphics().poly([0, 0, 5, 0, 2.5, 3]).fill(mixHex(BEAK, DARK, 0.1));
  return { body, face, mouth, mouthPivot: { x: 14, y: -8 } };
}

// ── 16) BEE — black abdomen stripes, two gossamer wings, antennae, a tiny stinger. Bzzz, busy busy. ──
function buildBee(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Gossamer wings (behind the body). Art revision 2026-06-23: a soft blue-grey OUTLINE + a faint vein so
  // they keep their shape against a light floor (a pure-white stroke used to vanish), plus a touch more
  // body so they read as wings, not smudges.
  for (const [wx, wy, rx, ry] of [
    [-6, -10, 9.5, 6.3],
    [8, -12, 8.5, 5.3],
  ] as const) {
    body
      .ellipse(wx, wy, rx, ry)
      .fill({ color: 0xeaf4ff, alpha: 0.62 })
      .ellipse(wx, wy, rx, ry)
      .stroke({ color: 0x8fa9cc, width: 1.2, alpha: 0.75 })
      .moveTo(wx, wy)
      .lineTo(wx + (wx < 0 ? -rx * 0.7 : rx * 0.7), wy)
      .stroke({ color: 0x8fa9cc, width: 0.8, alpha: 0.4 });
  }
  // Stinger at the tail.
  body.poly([-22, 2, -29, 0, -22, -2]).fill(DARK);
  // Tiny legs.
  body.roundRect(-6, 16, 4, 7, 2).roundRect(2, 16, 4, 7, 2).fill(DARK);
  drawBeanBody(body, p, 0.12);
  // Black abdomen stripes (the bee signature).
  for (const sx of [-10, -1, 8]) body.ellipse(sx, 2, 3, 16).fill({ color: DARK, alpha: 0.85 });
  drawHead(body, p);
  // Antennae with little bobbles (Art revision 2026-06-23: thicker stalks + bigger bobbles so they read).
  body.moveTo(10, -24).quadraticCurveTo(6, -32, 4, -33).stroke({ color: DARK, width: 1.7 });
  body.moveTo(18, -24).quadraticCurveTo(22, -32, 24, -33).stroke({ color: DARK, width: 1.7 });
  body.circle(4, -33, 2).fill(DARK).circle(24, -33, 2).fill(DARK);
  const stampFace = (g: Graphics): void => {
    drawFaceEyes(g, p, 2.5);
    // Rosy cheeks + a happy little smile.
    g.circle(7, -12, 2).fill({ color: 0xff9bb0, alpha: 0.4 });
    g.circle(21, -12, 2).fill({ color: 0xff9bb0, alpha: 0.4 });
    g.moveTo(10, -10).quadraticCurveTo(14, -6, 18, -10).stroke({ color: MOUTH_IN, width: 1.3, alpha: 0.6 });
  };
  stampFace(body);
  const face = new Graphics();
  stampFace(face);
  const mouth = new Graphics().moveTo(0, 0).bezierCurveTo(3, 2.5, 7, 2.5, 10, 0).lineTo(10, 1.5).bezierCurveTo(7, 4, 3, 4, 0, 1.5).closePath().fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 9, y: -7 } };
}

// ── 17) CRAB — two big claws reaching out, eyes on stalks, a wide shell, scuttly legs. Mildly crabby. ──
function buildCrab(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Scuttly walking legs out each side.
  for (const lx of [-16, -12, 12, 16]) body.moveTo(lx, 10).lineTo(lx + (lx < 0 ? -6 : 6), 18).stroke({ color: p.shade, width: 2.4 });
  // Left claw on an arm.
  body.moveTo(-14, 2).lineTo(-24, -2).stroke({ color: p.shade, width: 4 });
  body.ellipse(-27, -3, 5, 6).fill(p.shade).ellipse(-27, -3, 4, 5).fill(p.hex);
  body.poly([-31, -5, -24, -4, -29, -1]).fill(p.shade); // pincer gap
  // Right claw on an arm.
  body.moveTo(16, 2).lineTo(26, -2).stroke({ color: p.shade, width: 4 });
  body.ellipse(29, -3, 5, 6).fill(p.shade).ellipse(29, -3, 4, 5).fill(p.hex);
  body.poly([33, -5, 26, -4, 31, -1]).fill(p.shade);
  drawBeanBody(body, p, 0.06);
  // Wide shell sheen.
  body.ellipse(0, -4, 16, 8).fill({ color: p.hi, alpha: 0.3 });
  drawHead(body, p);
  // Eyestalks up top.
  body.moveTo(10, -22).lineTo(8, -30).stroke({ color: p.shade, width: 2.2 });
  body.moveTo(18, -22).lineTo(20, -30).stroke({ color: p.shade, width: 2.2 });
  const stampFace = (g: Graphics): void => {
    g.circle(8, -31, 2.6).fill(WHITE);
    drawEye(g, 8, -31, 2);
    g.circle(20, -31, 2.6).fill(WHITE);
    drawEye(g, 20, -31, 2);
    // A wide, faintly crabby mouth.
    g.ellipse(14, -9, 6, 1.6).fill({ color: MOUTH_IN, alpha: 0.5 });
  };
  stampFace(body);
  const face = new Graphics();
  stampFace(face);
  const mouth = new Graphics().moveTo(0, 0).lineTo(12, 0).lineTo(11, 2.5).lineTo(1, 2.5).closePath().fill(p.shade);
  return { body, face, mouth, mouthPivot: { x: 8, y: -8 } };
}

// ── 18) SHARK — a big dorsal fin, a toothy grin, gill slashes, derpy wide eyes. Friend-shaped, mostly. ──
function buildShark(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Tail fin behind.
  body.poly([-18, 2, -30, -8, -28, 2, -30, 12]).fill(p.shade).poly([-17, 2, -28, -6, -26, 2, -28, 10]).fill(p.hex);
  // Lower pectoral fin.
  body.poly([-2, 14, 6, 22, 10, 14]).fill(p.shade);
  drawBeanBody(body, p, 0);
  // Pale belly.
  body.ellipse(3, 8, 14, 9).fill({ color: 0xeef3f7, alpha: 0.55 });
  // Big dorsal fin on top.
  body.poly([-6, -14, 2, -30, 8, -14]).fill(p.shade).poly([-5, -14, 2, -28, 7, -14]).fill(p.hex);
  drawHead(body, p);
  // Gill slashes.
  for (const gx of [4, 7, 10]) body.moveTo(gx, -18).lineTo(gx - 1.5, -10).stroke({ color: p.shade, width: 1.2, alpha: 0.6 });
  const stampFace = (g: Graphics): void => {
    // Wide-set derpy eyes.
    drawEye(g, 19, -17, 2.4);
    drawEye(g, 8, -17, 2.4);
    // A big toothy grin (upper teeth).
    g.rect(5, -9, 19, 1).fill({ color: MOUTH_IN, alpha: 0.5 });
    for (const tx of [6, 11, 16, 21]) g.poly([tx, -9, tx + 2.6, -9, tx + 1.3, -6]).fill(WHITE);
  };
  stampFace(body);
  const face = new Graphics();
  stampFace(face);
  // Toothy lower jaw — hinges wide.
  const mouth = new Graphics();
  mouth.moveTo(0, 0).lineTo(18, 0).lineTo(17, 4).lineTo(1, 4).closePath().fill(p.shade);
  for (const tx of [2, 7, 12]) mouth.poly([tx, 0, tx + 2.6, 0, tx + 1.3, 3]).fill(WHITE);
  return { body, face, mouth, mouthPivot: { x: 5, y: -8 } };
}

// ── 19) BLOBFISH — the sad, gelatinous deep-sea meme: a big DROOPY bulbous nose hanging over a
// downturned frown, tiny hooded eyes, a melty saggy body. So-ugly-it's-lovable — the embodiment of Monday. ──
function buildBlobfish(p: CritterPalette): CritterArt {
  const body = new Graphics();
  // Little pectoral nub fins, slumped low on each side.
  body.ellipse(-18, 10, 5.5, 3.5).fill(p.shade).ellipse(-18, 9.5, 5.5, 3.5).fill(p.hex);
  body.ellipse(20, 12, 5, 3).fill(p.shade).ellipse(20, 11.5, 5, 3).fill(p.hex);
  // Soft gelatinous body with a heavy, melty sag at the base (gravity won) + a pale gelatin sheen.
  drawBeanBody(body, p, 0.22);
  body.ellipse(1, 13, 20, 9).fill({ color: p.shade, alpha: 0.22 });
  body.ellipse(2, 6, 16, 12).fill({ color: 0xffffff, alpha: 0.14 });
  drawHead(body, p);
  const noseHex = mixHex(p.hex, 0xcf6f92, 0.45); // a fleshier rose than the body so the schnoz reads
  const stampFace = (g: Graphics): void => {
    // Heavy hooded brows, drooping outward — the permanent unimpressed sag.
    g.moveTo(4, -17.5).quadraticCurveTo(9, -20.5, 13.5, -18.5).stroke({ color: p.shade, width: 2.4, alpha: 0.6 });
    g.moveTo(14.5, -18.5).quadraticCurveTo(19, -20.5, 23.5, -17).stroke({ color: p.shade, width: 2.4, alpha: 0.6 });
    // Tiny sad beady eyes, set under the hoods.
    drawEye(g, 18, -15.5, 1.8);
    drawEye(g, 9, -15.5, 1.8);
    // THE HERO — a big droopy bulbous nose hanging over the mouth (fleshy, with a shaded underside + sheen).
    g.ellipse(14, -3, 7.5, 8.5).fill(mixHex(noseHex, p.shade, 0.3)); // shaded underside
    g.ellipse(14, -4.5, 7.5, 7.8).fill(noseHex);
    g.ellipse(11.4, -8.5, 3, 3.2).fill({ color: 0xffffff, alpha: 0.35 }); // sheen
    g.ellipse(12.4, 0.6, 1.6, 1).fill({ color: p.shade, alpha: 0.4 }); // left nostril dimple
    g.ellipse(16, 0.6, 1.6, 1).fill({ color: p.shade, alpha: 0.4 }); // right nostril dimple
  };
  stampFace(body);
  const face = new Graphics();
  stampFace(face);
  // The iconic downturned FROWN, just under the drooping nose — hinges into an even deeper sigh.
  const mouth = new Graphics()
    .moveTo(0, 0)
    .quadraticCurveTo(6, -3.5, 12, 0)
    .lineTo(12, 1.8)
    .quadraticCurveTo(6, -1.7, 0, 1.8)
    .closePath()
    .fill({ color: MOUTH_IN, alpha: 0.5 });
  return { body, face, mouth, mouthPivot: { x: 8, y: 4 } };
}

// ── 20) CTHULHU — the intern from the deep: a squid-tentacle MAW over the mouth, big membranous BAT
// WINGS behind the shoulders, eerie glowing slit-eyes under a heavy brow, tentacle feet. Menace, but cute. ──
function buildCthulhu(p: CritterPalette): CritterArt {
  const body = new Graphics();
  const dark = mixHex(p.hex, DARK, 0.4);
  const membrane = mixHex(p.hex, DARK, 0.5);

  // ── BIG BAT WINGS behind the shoulders (the hero silhouette). Each is a membrane spanning three
  // finger-struts with a scalloped trailing edge + claw hooks. A darker FAR wing peeks behind a bolder
  // NEAR wing for depth. Drawn BEFORE the body so the wrist tucks behind the shoulders. ──
  const drawWing = (wristX: number, tipS: number, col: number, alpha: number): void => {
    const A: [number, number] = [wristX - 18 * tipS, -27];
    const B: [number, number] = [wristX - 25 * tipS, -13];
    const C: [number, number] = [wristX - 20 * tipS, 4];
    body
      .moveTo(wristX, -5)
      .lineTo(A[0], A[1])
      .quadraticCurveTo(wristX - 11 * tipS, -12, B[0], B[1]) // concave web scallop A→B
      .quadraticCurveTo(wristX - 11 * tipS, -2, C[0], C[1]) // concave web scallop B→C
      .quadraticCurveTo(wristX - 6 * tipS, 0, wristX, -5) // inner trailing edge back to the wrist
      .closePath()
      .fill({ color: col, alpha });
    for (const tip of [A, B, C]) {
      body.moveTo(wristX, -5).lineTo(tip[0], tip[1]).stroke({ color: dark, width: 1.8, alpha: 0.9 });
      body.circle(tip[0], tip[1], 1.5).fill(dark); // claw hook
    }
  };
  drawWing(-4, 0.9, mixHex(membrane, DARK, 0.3), 0.9); // far wing (darker, peeking up-right)
  drawWing(-8, 1.05, membrane, 0.96); // near wing (bolder, spread wider)

  // Tentacle feet at the base (three stubby tentacles curling out).
  for (const tx of [-8, 0, 8]) {
    body.moveTo(tx, 12).quadraticCurveTo(tx + 3, 22, tx - 2, 25).stroke({ color: p.shade, width: 4 });
    body.moveTo(tx, 12).quadraticCurveTo(tx + 3, 21, tx - 1, 24).stroke({ color: p.hex, width: 2.2 });
  }

  drawBeanBody(body, p, 0.06);
  // A darker mottled dorsal ridge for a scaly, from-the-deep read.
  body.ellipse(-3, -6, 14, 9).fill({ color: dark, alpha: 0.22 });
  drawHead(body, p);

  // The face-tentacle MAW hanging over the mouth (an appendage — reads as an accent under a skin): five
  // tapering strands with paler highlights + little suckers at the curling tips.
  const tHi = mixHex(p.hex, NOSE, 0.25);
  for (const [bx, curl, len] of [
    [5, -3, 7],
    [9, 1.5, 8.5],
    [13, -1.5, 9],
    [17, 1.5, 8.5],
    [21, 3, 7.5],
  ] as const) {
    const txp = bx + curl * 2;
    body.moveTo(bx, -8).quadraticCurveTo(bx + curl, -2, txp, len).stroke({ color: p.shade, width: 4.2 });
    body.moveTo(bx, -8).quadraticCurveTo(bx + curl, -2, txp, len).stroke({ color: tHi, width: 2.2 });
    body.circle(txp, len, 1.3).fill({ color: NOSE, alpha: 0.6 }); // curling sucker tip
  }

  const stampFace = (g: Graphics): void => {
    // Heavy menacing brow across the top of the face.
    g.moveTo(4, -19).lineTo(24, -20).stroke({ color: dark, width: 2.6, alpha: 0.85 });
    // Two big eerie GLOWING eyes: an acid-green halo, a bright sclera, a dark slit pupil + catch-light.
    for (const ex of [9, 18]) {
      g.circle(ex, -15, 4.2).fill({ color: 0x9dff5a, alpha: 0.32 });
      g.circle(ex, -15, 3).fill({ color: 0xd8ffb0 });
      g.ellipse(ex, -15, 1.3, 2.2).fill(DARK);
      g.circle(ex + 0.7, -15.8, 0.6).fill({ color: CATCH, alpha: 0.9 });
    }
  };
  stampFace(body);
  const face = new Graphics();
  stampFace(face);
  // A small jaw behind the tentacle maw — hinges for the eldritch murmur.
  const mouth = new Graphics().moveTo(0, 0).bezierCurveTo(3, 2, 8, 2, 11, 0).lineTo(11, 1.6).bezierCurveTo(8, 3.6, 3, 3.6, 0, 1.6).closePath().fill({ color: MOUTH_IN, alpha: 0.7 });
  return { body, face, mouth, mouthPivot: { x: 8, y: -6 } };
}

/** The registry — one builder per species. Keep keys in sync with `DUCK_SPECIES`/`SPECIES_META`. */
const SPECIES_BUILDERS: Record<DuckSpecies, (p: CritterPalette) => CritterArt> = {
  duck: buildDuck,
  frog: buildFrog,
  cat: buildCat,
  fox: buildFox,
  penguin: buildPenguin,
  pig: buildPig,
  axolotl: buildAxolotl,
  capybara: buildCapybara,
  hamster: buildHamster,
  dino: buildDino,
  // Wave 2 (ADR-0036).
  goose: buildGoose,
  shiba: buildShiba,
  sloth: buildSloth,
  raccoon: buildRaccoon,
  owl: buildOwl,
  bee: buildBee,
  crab: buildCrab,
  shark: buildShark,
  blobfish: buildBlobfish,
  cthulhu: buildCthulhu,
};

/**
 * Per-species overall SCALE — silhouette differentiation (Art Revision, Wave 3, finding "виды сливаются").
 * Every species shares ONE anchor rig (so cosmetics/held items/animation are universal), which makes the
 * round-bodied animals read alike at a glance. A gentle UNIFORM scale per species — applied to the whole
 * `body` by {@link Critter} so worn cosmetics ride along and stay aligned — restores "read at a distance":
 * the chonky capybara/dino feel BIG, the hamster/axolotl feel small, with ZERO geometry change and no
 * cosmetic-fit breakage. Because the skin/outfit layers are children of the scaled body, a full-body skin
 * also auto-fits each species' size (the safe core of finding S6). The duck stays EXACTLY 1.0 — the
 * beloved original is never rescaled (its byte-faithful recipe is untouched).
 */
export const SPECIES_SCALE: Record<DuckSpecies, number> = {
  duck: 1.0,
  frog: 0.96,
  cat: 1.0,
  fox: 1.02,
  penguin: 1.05,
  pig: 1.03,
  axolotl: 0.92,
  capybara: 1.12,
  hamster: 0.9,
  dino: 1.12,
  // Wave 2 (ADR-0036): goose/shark/blob read BIG, the bee reads tiny; the rest near the duck.
  goose: 1.06,
  shiba: 1.0,
  sloth: 1.03,
  raccoon: 1.0,
  owl: 0.98,
  bee: 0.85,
  crab: 1.0,
  shark: 1.05,
  blobfish: 1.05,
  cthulhu: 1.0,
};

/**
 * Build one critter's static body art + hinging mouth for a species at a chosen tint. Unknown species
 * fall back to the original duck so a future/legacy value still renders a beloved chick (never a blank).
 */
export function buildCritterArt(species: DuckSpecies, color: DuckColor): CritterArt {
  const builder = SPECIES_BUILDERS[species] ?? buildDuck;
  return builder(buildPalette(color));
}
